import React, { useState, useMemo } from "react";
import {
  Search,
  Eye,
  Trash2,
  X,
  Phone,
  Package,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Calendar,
} from "lucide-react";
import { useGetUsersQuery, useDeleteUserMutation } from "../store/api/adminApi";
import { useToast } from "../components/Toast";

const PAGE_SIZE = 10;

function Avatar({ name = "?", size = 36 }) {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className="rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size }}
    >
      <span
        className="font-manrope-bold text-primary"
        style={{ fontSize: size * 0.36 }}
      >
        {initials}
      </span>
    </div>
  );
}

function DeleteModal({ user, onCancel, onConfirm, isLoading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative bg-white rounded-2xl p-8 w-[340px] flex flex-col items-center text-center shadow-2xl">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <Trash2 size={28} className="text-primary" strokeWidth={1.8} />
        </div>
        <h3 className="text-[16px] font-manrope-bold text-primary mb-2">
          Delete user?
        </h3>
        <p className="text-[12px] font-manrope-regular text-gray-400 mb-1 leading-relaxed">
          You are about to delete
        </p>
        <p className="text-[13px] font-manrope-bold text-gray-800 mb-4">
          {user?.name}
        </p>
        <p className="text-[12px] font-manrope-regular text-gray-400 mb-6 leading-relaxed">
          This action cannot be undone.
        </p>
        <div className="flex gap-3 w-full">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-manrope-semibold text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 py-2.5 rounded-xl bg-primary text-white text-[13px] font-manrope-semibold hover:opacity-90 transition-all disabled:opacity-60"
          >
            {isLoading ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserDetailsPanel({ user, onClose, onDelete }) {
  const joinedDate = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "—";

  return (
    <div className="w-[300px] bg-white border-l border-gray-100 flex flex-col fixed right-0 top-0 h-full z-40 shadow-xl">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h2 className="text-[15px] font-manrope-bold text-gray-900">
          User details
        </h2>
        <button
          onClick={onClose}
          className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all"
        >
          <X size={15} className="text-gray-500" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5">
        {/* Identity */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Avatar name={user.name} size={42} />
            <div>
              <p className="text-[13px] font-manrope-bold text-gray-900">
                {user.name}
              </p>
              <p className="text-[11px] font-manrope-regular text-gray-400 break-all">
                {user.email}
              </p>
            </div>
          </div>
          <button
            onClick={onDelete}
            className="w-7 h-7 rounded-md bg-red-50 flex items-center justify-center hover:bg-red-100 transition-all flex-shrink-0"
          >
            <Trash2 size={14} className="text-primary" strokeWidth={1.8} />
          </button>
        </div>

        <div className="border-t border-gray-100 pt-4 flex flex-col gap-3 mb-5">
          {/* Phone */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0">
              <Phone size={14} className="text-gray-500" strokeWidth={1.6} />
            </div>
            <span className="text-[12px] font-manrope-regular text-gray-600">
              {user.phone || "—"}
            </span>
          </div>

          {/* Joined */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0">
              <Calendar size={14} className="text-gray-500" strokeWidth={1.6} />
            </div>
            <span className="text-[12px] font-manrope-regular text-gray-600">
              Joined {joinedDate}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-3">
          <div className="flex-1 bg-gray-50 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Package size={13} className="text-gray-400" strokeWidth={1.6} />
              <span className="text-[10px] font-manrope-regular text-gray-400">
                Bookings
              </span>
            </div>
            <p className="text-[20px] font-manrope-bold text-gray-900">
              {user.totalBookings}
            </p>
          </div>
          <div className="flex-1 bg-gray-50 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <DollarSign
                size={13}
                className="text-gray-400"
                strokeWidth={1.6}
              />
              <span className="text-[10px] font-manrope-regular text-gray-400">
                Total spent
              </span>
            </div>
            <p className="text-[20px] font-manrope-bold text-gray-900">
              ${user.totalSpent?.toFixed(2)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Users() {
  const [search, setSearch] = useState("");
  const [liveSearch, setLiveSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, isError } = useGetUsersQuery();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const toast = useToast();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const users = data?.data ?? [];

  const filtered = useMemo(() => {
    const q = liveSearch.toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.phone?.toLowerCase().includes(q),
    );
  }, [users, liveSearch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const handleSearch = () => {
    setLiveSearch(search);
    setCurrentPage(1);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUser(deleteTarget.id).unwrap();
      setDeleteTarget(null);
      if (selectedUser?.id === deleteTarget.id) setSelectedUser(null);
      toast.success("User deleted successfully.");
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to delete user.");
    }
  };

  // Build visible page numbers
  const pageNumbers = useMemo(() => {
    if (totalPages <= 5)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 3) return [1, 2, 3, "...", totalPages];
    if (currentPage >= totalPages - 2)
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", currentPage, "...", totalPages];
  }, [totalPages, currentPage]);

  return (
    <>
      <div
        className={`flex flex-col min-h-screen transition-all ${selectedUser ? "mr-[300px]" : ""}`}
      >
        {/* Header */}
        <header className="flex items-center justify-between px-8 pt-7 pb-4">
          <div>
            <h1 className="text-[22px] font-manrope-bold text-gray-900">
              Users management
            </h1>
            <p className="text-[12px] font-manrope-regular text-gray-400 mt-0.5">
              Manage all platform users from here.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center shadow-sm">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                className="text-gray-600"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-manrope-bold text-primary">
                  V
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-[13px] font-manrope-semibold text-gray-900 leading-tight">
                  Vigelanto
                </span>
                <span className="text-[11px] font-manrope-regular text-gray-400 leading-tight">
                  Admin
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 px-8 pb-8">
          {/* Search */}
          <div className="flex items-center gap-3 mb-5">
            <div className="relative flex-1 max-w-xs">
              <input
                type="text"
                placeholder="Search by name, email or phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="w-full pl-4 pr-4 py-2.5 bg-white border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
              />
            </div>
            <button
              onClick={handleSearch}
              className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center hover:opacity-90 transition-all"
            >
              <Search size={16} className="text-white" strokeWidth={2} />
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl overflow-hidden">
            {/* Head */}
            <div className="grid grid-cols-[2fr_2.2fr_1.4fr_1.1fr_1.2fr_1.4fr_0.9fr] px-5 py-3 border-b border-gray-50">
              {[
                "Name",
                "Email",
                "Phone",
                "Bookings",
                "Total spent",
                "Joined",
                "Action",
              ].map((col) => (
                <span
                  key={col}
                  className="text-[12px] font-manrope-semibold text-gray-400"
                >
                  {col}
                </span>
              ))}
            </div>

            {/* Loading */}
            {isLoading && (
              <div className="flex items-center justify-center py-16">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {/* Error */}
            {isError && (
              <div className="flex items-center justify-center py-16">
                <p className="text-[13px] font-manrope-regular text-gray-400">
                  Failed to load users.
                </p>
              </div>
            )}

            {/* Empty */}
            {!isLoading && !isError && filtered.length === 0 && (
              <div className="flex items-center justify-center py-16">
                <p className="text-[13px] font-manrope-regular text-gray-400">
                  No users found.
                </p>
              </div>
            )}

            {/* Rows */}
            {!isLoading &&
              !isError &&
              paginated.map((user) => (
                <div
                  key={user.id}
                  className="grid grid-cols-[2fr_2.2fr_1.4fr_1.1fr_1.2fr_1.4fr_0.9fr] px-5 py-3 border-b border-gray-50 hover:bg-gray-50/50 transition-all items-center"
                >
                  <div className="flex items-center gap-2.5">
                    <Avatar name={user.name} size={32} />
                    <span className="text-[13px] font-manrope-semibold text-gray-800 truncate">
                      {user.name}
                    </span>
                  </div>
                  <span className="text-[12px] font-manrope-regular text-gray-500 truncate">
                    {user.email}
                  </span>
                  <span className="text-[12px] font-manrope-regular text-gray-500">
                    {user.phone || "—"}
                  </span>
                  <span className="text-[12px] font-manrope-semibold text-gray-700">
                    {user.totalBookings}
                  </span>
                  <span className="text-[12px] font-manrope-semibold text-gray-700">
                    ${user.totalSpent?.toFixed(2)}
                  </span>
                  <span className="text-[12px] font-manrope-regular text-gray-400">
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "—"}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedUser(user)}
                      className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all"
                    >
                      <Eye
                        size={14}
                        className="text-gray-500"
                        strokeWidth={1.6}
                      />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(user)}
                      className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-all"
                    >
                      <Trash2
                        size={14}
                        className="text-primary"
                        strokeWidth={1.6}
                      />
                    </button>
                  </div>
                </div>
              ))}

            {/* Footer */}
            {!isLoading && !isError && (
              <div className="flex items-center justify-between px-5 py-4">
                <span className="text-[12px] font-manrope-regular text-gray-500">
                  Total{" "}
                  <span className="font-manrope-bold text-gray-800">
                    {filtered.length}
                  </span>{" "}
                  users
                </span>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"
                  >
                    <ChevronsLeft size={14} />
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"
                  >
                    <ChevronLeft size={14} />
                  </button>

                  {pageNumbers.map((p, i) =>
                    p === "..." ? (
                      <span
                        key={`e-${i}`}
                        className="w-7 h-7 flex items-center justify-center text-[12px] text-gray-400"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`w-7 h-7 rounded-md flex items-center justify-center text-[12px] font-manrope-semibold transition-all ${
                          currentPage === p
                            ? "bg-primary text-white"
                            : "text-gray-500 hover:bg-gray-100"
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}

                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"
                  >
                    <ChevronRight size={14} />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"
                  >
                    <ChevronsRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedUser && (
        <UserDetailsPanel
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onDelete={() => setDeleteTarget(selectedUser)}
        />
      )}

      {deleteTarget && (
        <DeleteModal
          user={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          isLoading={isDeleting}
        />
      )}
    </>
  );
}
